import { UserRole } from '../../models/organization/dto/invite-to-organization.dto';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import { canManageOrgResources } from '../../routes/access-policy';
import { useAuth } from './AuthProvider';

type MaybeUser = (Record<string, unknown> & { role?: UserRole }) | null | undefined;

/**
 * Role of the signed-in dashboard user, read from the `role` custom claim the
 * API attaches on org-member creation (see `setCustomUserClaims` call sites).
 *
 * Anything that isn't exactly the coach claim normalizes to ADMIN:
 * - Missing claim → ADMIN, deliberately fail-open: onboarding forces you to be
 *   admin (a user who creates an organization becomes its admin member), and
 *   older accounts predate the claim entirely.
 * - Unrecognized claim value (future role, tampered token) → ADMIN too, so
 *   every consumer agrees on one of the two known roles. Disagreement here
 *   previously caused an infinite redirect loop between ProtectedRoute
 *   (rejected the unknown role) and RedirectAuthorizedUser (sent it to the
 *   admin landing). The API enforces real permissions regardless of what the
 *   client believes.
 *
 * NOTE: this is the CLAIM-ONLY answer, and the claim is a single global value
 * per user. Prefer `useUserRole()`, which prefers the selected organization's
 * server-resolved role. Use this directly only where no organization has been
 * selected yet (sign-in landing, invitation acceptance).
 */
export function getUserRole(user: MaybeUser): UserRole {
    return user?.role === UserRole.COACH ? UserRole.COACH : UserRole.ADMIN;
}

/**
 * Resolve the effective role from the per-org answer, falling back to the claim.
 *
 * The `role` custom claim is ONE value per Firebase user — the last
 * `setCustomUserClaims` wins — so it structurally cannot describe a user who is
 * ADMIN of org A and COACH of org B. Two live consequences before this existed:
 *
 * 1. A coach in org B whose claim said `admin` (because they are an admin
 *    elsewhere, or because they accepted the invite on an existing account —
 *    `accept-invitation-authenticated` deliberately does not stamp claims) saw
 *    the FULL admin dashboard for org B, i.e. buttons the API 403s.
 * 2. Conversely an admin of org A who became a coach in org B lost admin UI
 *    everywhere.
 *
 * `myRole` comes from the caller's `OrganizationMember` row for the selected
 * org, which is the same source the API authorizes against — so the UI and
 * `hasOrganizationAdminAccess` now agree by construction.
 *
 * Null/undefined `orgRole` means "unknown" rather than "not an admin", so it
 * falls back to the claim rather than failing closed. That case is
 * elevated/platform admins (no membership row, authorized by the
 * ELEVATED_ADMIN_EMAILS bypass) and the brief window before `my-organizations`
 * resolves. A coach always has a membership row, so a coach always gets a
 * concrete `myRole`.
 */
export function resolveUserRole(
    claimUser: MaybeUser,
    orgRole: UserRole | null | undefined,
): UserRole {
    if (orgRole === UserRole.COACH) return UserRole.COACH;
    if (orgRole === UserRole.ADMIN) return UserRole.ADMIN;
    return getUserRole(claimUser);
}

/**
 * Role of the signed-in user IN THE CURRENTLY SELECTED ORGANIZATION.
 *
 * This is what every in-app affordance should gate on. See `resolveUserRole`
 * for why the claim alone is not enough.
 */
export function useUserRole(): UserRole {
    const { currentUser } = useAuth();
    const { selectedOrganization } = useSelectedOrganization();
    return resolveUserRole(currentUser, selectedOrganization?.myRole);
}

/**
 * Can the signed-in user perform org-management actions? Convenience wrapper
 * over `canManageOrgResources` for pages that gate admin-only sections
 * (payment tables, destructive buttons) — mirror of the API's
 * `hasOrganizationAdminAccess`, so the UI never offers what the API 403s.
 */
export function useIsOrgManager(): boolean {
    return canManageOrgResources(useUserRole());
}
